import type { ReactElement, ReactNode } from 'react';
import { Link } from 'react-router-dom';
import { useQuery } from '@tanstack/react-query';
import {
  fetchCosts,
  fetchDashboard,
  fetchSparklines,
  type CostBucket,
  type DashboardResponse,
  type SparklineMetric,
  type SparklineResponse,
} from '../api/admin.js';
import {
  formatDurationMs,
  formatNumber,
  formatPercent,
  nanoToUsdCompact,
  formatDateTime,
  shortId,
} from '../lib/format.js';
import { Sparkline } from '../components/charts/Sparkline.js';
import { CostAreaChart } from '../components/charts/CostAreaChart.js';

export function DashboardPage(): ReactElement {
  const summary = useQuery({
    queryKey: ['admin', 'dashboard'],
    queryFn: fetchDashboard,
    refetchInterval: 15_000,
  });
  const sparklines = useQuery({
    queryKey: ['admin', 'dashboard', 'sparklines'],
    queryFn: fetchSparklines,
    refetchInterval: 60_000,
  });
  const costs = useQuery({
    queryKey: ['admin', 'costs', 'daily', 'by-kind'],
    queryFn: () => fetchCosts({ groupBy: 'kind' }),
    refetchInterval: 60_000,
  });

  return (
    <>
      <div className="page-header">
        <div>
          <h1 className="page-title">Dashboard</h1>
          <p className="page-sub">KPIs últimas 24 horas · refresh cada 15s</p>
        </div>
      </div>
      {summary.isPending ? (
        <div className="loading" data-testid="dashboard-loading">
          Cargando…
        </div>
      ) : summary.isError ? (
        <div className="loading" data-testid="dashboard-error">
          Error: {summary.error.message}
        </div>
      ) : (
        <DashboardContent
          data={summary.data}
          sparklines={sparklines.data ?? null}
          costs={costs.data ?? null}
        />
      )}
    </>
  );
}

interface DashboardContentProps {
  data: DashboardResponse;
  sparklines: SparklineResponse | null;
  costs: CostBucket[] | null;
}

function DashboardContent({ data, sparklines, costs }: DashboardContentProps): ReactElement {
  const { kpis, topUsers, liveSessions, recentErrors } = data;
  const m = sparklines?.metrics;
  return (
    <div data-testid="dashboard-content">
      <div className="dash-kpis">
        <KpiCard
          label="Sessions 24h"
          value={formatNumber(kpis.sessionCount)}
          spark={m?.sessionCount}
          formatDelta={formatNumber}
        />
        <KpiCard
          label="Turns 24h"
          value={formatNumber(kpis.turnCount)}
          spark={m?.turnCount}
          formatDelta={formatNumber}
        />
        <KpiCard
          label="Cost 24h"
          value={nanoToUsdCompact(kpis.totalCostNano)}
          spark={
            m?.totalCostNano
              ? toNumberMetric(m.totalCostNano)
              : undefined
          }
          formatDelta={(n) => nanoToUsdCompact(String(n))}
        />
        <KpiCard
          label="Errors 24h"
          value={formatNumber(kpis.errorCount)}
          spark={m?.errorCount}
          formatDelta={formatNumber}
        />
        <KpiCard
          label="Error rate"
          value={formatPercent(kpis.errorRate)}
          sub={`${kpis.errorCount} / ${kpis.turnCount}`}
        />
        <KpiCard
          label="p95 turn"
          value={formatDurationMs(kpis.p95TurnDurationMs ?? null)}
        />
      </div>

      <div className="dash-grid">
        <section className="card">
          <header className="card-head">
            <h3 className="card-title">
              Cost over time <small>by kind · last days</small>
            </h3>
          </header>
          <CostAreaChart rows={costs ?? []} />
        </section>

        <section className="card">
          <header className="card-head">
            <h3 className="card-title">
              Live sessions <small>{liveSessions.length} abiertas</small>
            </h3>
          </header>
          <div className="grid-list">
            {liveSessions.length === 0 ? (
              <div className="empty">Sin sesiones live</div>
            ) : (
              liveSessions.slice(0, 8).map((s) => (
                <Link key={s.id} to={`/sessions/${s.id}`} className="grid-list-row">
                  <span className="flex ai-c gap-3">
                    <span className="dot-live" />
                    <span>
                      {s.email} <span className="tt-tert">· {s.moduleId ?? '—'}</span>
                    </span>
                  </span>
                  <span className="mono tt-tert">{formatDateTime(s.startedAt)}</span>
                </Link>
              ))
            )}
          </div>
        </section>

        <section className="card">
          <header className="card-head">
            <h3 className="card-title">
              Top users <small>by spend (24h)</small>
            </h3>
          </header>
          <div className="grid-list">
            {topUsers.length === 0 ? (
              <div className="empty">Sin datos</div>
            ) : (
              topUsers.map((u) => (
                <Link key={u.userId} to={`/users/${u.userId}`} className="grid-list-row">
                  <span>{u.email}</span>
                  <span className="mono tt-sec">{nanoToUsdCompact(u.totalCostNano)}</span>
                </Link>
              ))
            )}
          </div>
        </section>

        <section className="card">
          <header className="card-head">
            <h3 className="card-title">
              Recent errors <small>últimos 20</small>
            </h3>
          </header>
          <div className="grid-list">
            {recentErrors.length === 0 ? (
              <div className="empty">Sin errors recientes</div>
            ) : (
              recentErrors.slice(0, 8).map((e) => (
                <Link
                  key={e.id}
                  to={e.sessionId ? `/sessions/${e.sessionId}` : '#'}
                  className="grid-list-row"
                  data-testid={`recent-error-${e.id}`}
                >
                  <span className="flex ai-c gap-3">
                    <span className="badge b-error">{e.errorKind ?? 'error'}</span>
                    <span className="mono tt-sec">{e.kind}</span>
                    <span className="tt-tert">{e.name}</span>
                  </span>
                  <span className="mono tt-tert">
                    {shortId(e.traceId, 8)} · {formatDateTime(e.startedAt)}
                  </span>
                </Link>
              ))
            )}
          </div>
        </section>
      </div>
    </div>
  );
}

interface KpiCardProps {
  label: string;
  value: string;
  sub?: string | undefined;
  spark?: SparklineMetric<number> | undefined;
  formatDelta?: ((n: number) => string) | undefined;
}

function KpiCard({ label, value, sub, spark, formatDelta }: KpiCardProps): ReactElement {
  const slug = label.toLowerCase().replace(/[^a-z0-9]+/g, '-');
  return (
    <div className="kpi" data-testid={`kpi-${slug}`}>
      <span className="kpi-label">{label}</span>
      <div className="kpi-value-row">
        <span className="kpi-value">{value}</span>
        {spark ? <DeltaBadge metric={spark} formatDelta={formatDelta} /> : null}
      </div>
      {spark ? (
        <div className="kpi-spark">
          <Sparkline values={spark.buckets} className="spark" />
        </div>
      ) : sub ? (
        <span className="kpi-sub">{sub}</span>
      ) : null}
    </div>
  );
}

interface DeltaBadgeProps {
  metric: SparklineMetric<number>;
  formatDelta?: ((n: number) => string) | undefined;
}

function DeltaBadge({ metric, formatDelta }: DeltaBadgeProps): ReactElement | null {
  const delta = metric.current - metric.prev;
  const ratio = metric.prev > 0 ? delta / metric.prev : null;
  if (metric.prev === 0 && metric.current === 0) return null;
  const tone = delta > 0 ? 'up' : delta < 0 ? 'down' : 'neutral';
  const symbol: ReactNode = tone === 'up' ? '▲' : tone === 'down' ? '▼' : '·';
  const label = ratio === null
    ? (formatDelta ? formatDelta(delta) : String(delta))
    : `${ratio > 0 ? '+' : ''}${(ratio * 100).toFixed(0)}%`;
  return (
    <span className={`delta ${tone}`}>
      {symbol} {label}
    </span>
  );
}

function toNumberMetric(metric: SparklineMetric<string>): SparklineMetric<number> {
  return {
    current: Number(metric.current || '0'),
    prev: Number(metric.prev || '0'),
    buckets: metric.buckets.map((v) => Number(v || '0')),
  };
}
